import { Suspense, lazy } from 'react'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  applicableProperties,
  isDrawable,
  pruneInapplicable,
  resolve,
  type Property,
  type Schema,
} from '../model/schema'
import { ComponentRefField } from './ComponentRefField'
import { EdgeField } from './EdgeField'
import { useAuthoringScope } from './scope'
import { humanise } from '../model/labels'
import { typeOf } from '../model/manifest'
import { OptionList } from '../ui/OptionList'

/**
 * The rich text control, loaded only when a form actually meets one.
 *
 * TinyMCE is around 370 KB gzipped, and most forms are text boxes and
 * selects. The boundary sits here for the same reason it sits around
 * `ContentEditor`: neither screen should have to know which controls are
 * heavy, and a form that draws no rich text must not pay for one.
 */
const LazyRichText = lazy(async () => ({
  default: (await import('./RichTextField')).RichTextField,
}))

/**
 * Controls for one object schema — the whole of what this client knows
 * about drawing a form.
 *
 * **It draws what it understands and reports only that.** A property it
 * cannot draw — a list of records, a shape nested past the cap — renders
 * nothing, and its stored value rides through untouched because every
 * change is reported as the *whole* object with one member replaced. That matters
 * more than it sounds: the intents this form feeds are `set`-shaped, so
 * their fold assigns the whole struct and a member this form quietly
 * dropped would be cleared on the wire rather than left alone.
 *
 * There is no validation here beyond what a control can enforce by being
 * the control it is. The fold is the authority on what it will accept,
 * and a second copy of its rules in TypeScript would be a second model of
 * the same decision — wrong in a different way as soon as one moved.
 *
 * **What it does draw is only what applies.** A shape whose members are
 * discriminated says so on the wire, and the form asks the deciding
 * question and then offers the fields that go with the answer — a Link
 * asks where it points and then offers one address, rather than offering
 * an address for every kind and letting the author find out which one
 * was right. That is not validation moving here: the rule is still the
 * type's, it is just being read instead of guessed at.
 */
export function SchemaForm({
  schema,
  root,
  value,
  onChange,
  disabled,
  depth = 0,
}: {
  schema: Schema | undefined
  /** The schema the manifest published, which is where `$defs` lives. */
  root: Schema
  value: Record<string, unknown>
  /** The whole object, with one member replaced. */
  onChange: (next: Record<string, unknown>) => void
  disabled: boolean
  /** How deep this form already sits. Callers start at the default. */
  depth?: number
}) {
  const properties = applicableProperties(schema, root, value)
  if (properties.length === 0) return null

  return (
    <Stack spacing={2}>
      {properties.map((property) => (
        <PropertyControl
          key={property.name}
          property={property}
          root={root}
          value={value[property.name]}
          // Pruned on every change rather than only on a discriminator's,
          // because the form cannot tell the two apart without knowing
          // which member decides — and pruning a value that still applies
          // is a no-op. What it removes is what the author can no longer
          // see: an address typed under one kind, left behind by a change
          // to another, and refused by the fold with a sentence about a
          // box that is no longer on the screen.
          onChange={(next) => onChange(pruneInapplicable(schema, root, { ...value, [property.name]: next }))}
          disabled={disabled}
          depth={depth}
        />
      ))}
    </Stack>
  )
}

/**
 * How far a nested shape is drawn before the form stops recursing.
 *
 * A schema may contain itself — a website's routing tree does — and
 * `$ref` resolution alone would follow that cycle until the stack ran
 * out. Depth is the cheap guard, and two levels is what the registered
 * types actually need: a presentation with a background inside it.
 */
const MAXIMUM_DEPTH = 2

/**
 * What the empty string means in these vocabularies, said out loud.
 *
 * **It depends on whose choice it is, and the holder's role says.** A
 * layout's optional choices are presentation — a spacing, a background
 * — and leaving one unset hands it to the theme. Anything else's is the
 * author's: an event with no category has none, and a form with no
 * delivery kind sends nothing, and telling either author that a theme
 * decides is untrue. A form that is filling no type's intent — a create,
 * a record's first fields — has no holder and says only that nothing is
 * chosen, which is true of every role.
 *
 * The role is a proxy rather than a declaration: a record whose type
 * declared a presentation choice of its own would be told "(none)" where
 * the theme does in fact decide. Nothing in the manifest yet says what
 * an omitted value means per member.
 */
const THEME_DECIDES = '(the theme decides)'
const NOTHING_CHOSEN = '(none)'

function PropertyControl({
  property,
  root,
  value,
  onChange,
  disabled,
  depth,
}: {
  property: Property
  root: Schema
  value: unknown
  onChange: (next: unknown) => void
  disabled: boolean
  depth: number
}) {
  // The gate, before the switch and shared with `isFormable`, so that
  // "this section offers a form" and "this property gets a control" can
  // never disagree. The switch's own default is belt and braces.
  const { tenant, manifest, holder } = useAuthoringScope()

  if (!isDrawable(property.schema, root)) return null

  const schema = resolve(property.schema, root)
  if (!schema) return null

  const label = humanise(property.name)

  // A vocabulary before a type, because an enum of strings is a choice
  // rather than free text and drawing it as a text box would invite a
  // value the fold refuses.
  if (schema.enum) {
    const current = typeof value === 'string' ? value : ''
    // A stored value the build no longer declares is offered rather than
    // silently blanked — the same call `slotsToShow` makes about an
    // undeclared slot. Hiding it would show an author an empty control
    // over a document that holds something.
    const options = current === '' || schema.enum.includes(current) ? schema.enum : [...schema.enum, current]
    const unset = holder && typeOf(manifest, holder)?.role === 'layout' ? THEME_DECIDES : NOTHING_CHOSEN

    return (
      <TextField
        select
        label={label}
        required={property.required}
        value={current}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        fullWidth
      >
        {options.map((option) => (
          <MenuItem key={option || 'unset'} value={option}>
            {option === '' ? unset : option}
            {schema.enum?.includes(option) ? '' : ' — stored, not declared'}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  // A reference before an object, because its members are not the
  // author's to fill in: the component is, and the rest follows from the
  // edge the marker names.
  if (schema['x-edge']) {
    return (
      <EdgeField
        label={label}
        edge={schema['x-edge']}
        tenant={tenant}
        manifest={manifest}
        holder={holder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    )
  }

  // A component id before a string, for the reason a vocabulary comes
  // before one: a bare id is the single string an author cannot write,
  // and a text box here asks for a UUID.
  if (schema['x-component-role']) {
    return (
      <ComponentRefField
        label={label}
        role={schema['x-component-role']}
        tenant={tenant}
        manifest={manifest}
        value={typeof value === 'string' ? value : ''}
        onChange={(next) => onChange(next === '' ? undefined : next)}
        disabled={disabled}
        required={property.required}
      />
    )
  }

  // A type's name, offered from the manifest's types of the role the
  // marker names. Any other name is refused, so a text box would be a
  // control over values the platform never accepts.
  if (schema['x-type-role']) {
    const role = schema['x-type-role']
    const current = typeof value === 'string' ? value : ''
    const offered = (manifest?.types ?? [])
      .filter((entry) => entry.role === role)
      .map((entry) => entry.name)
      .sort()
    const options = current === '' || offered.includes(current) ? offered : [...offered, current]
    return (
      <TextField
        select
        label={label}
        required={property.required}
        value={current}
        onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
        disabled={disabled}
        helperText={offered.length === 0 ? `This tenant declares no ${role} type yet.` : undefined}
        fullWidth
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
            {offered.includes(option) ? '' : ` — stored, not a ${role}`}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  switch (schema.type) {
    case 'string':
      // The format is what tells one string from another, and it is on
      // the wire rather than in a list here — which is what lets a
      // tenant-defined type declare a `richText` or a `date` field and
      // get the right control without this client learning its name.
      if (schema.format === 'html') {
        return (
          <Suspense fallback={<LinearProgress />}>
            <LazyRichText
              label={label}
              value={typeof value === 'string' ? value : ''}
              onChange={onChange}
              disabled={disabled}
            />
          </Suspense>
        )
      }
      if (schema.format === 'date' || schema.format === 'date-time') {
        return (
          <TextField
            type={schema.format === 'date' ? 'date' : 'datetime-local'}
            label={label}
            required={property.required}
            value={typeof value === 'string' ? dateInputValue(value, schema.format) : ''}
            onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
            disabled={disabled}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: 260 }}
          />
        )
      }
      return (
        <TextField
          label={label}
          required={property.required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          // Shown rather than filled in. A declared default is what the
          // field holds while nobody has written it, so an empty box over
          // one is a lie — and prefilling it would send the value back as
          // something the author chose, which stops it following the
          // declaration the day it moves.
          placeholder={placeholderFor(schema)}
          fullWidth
        />
      )

    case 'integer':
    case 'number':
      return (
        <TextField
          type="number"
          label={label}
          required={property.required}
          value={typeof value === 'number' ? String(value) : ''}
          // A number, never the string the input holds: the payload is
          // JSON and a quoted number is a different value to the fold.
          // An emptied input is `undefined` — the member goes unsent —
          // rather than NaN, which does not survive serialisation and
          // would arrive as null.
          onChange={(event) => onChange(numberOrNothing(event.target.value))}
          disabled={disabled}
          sx={{ maxWidth: 220 }}
        />
      )

    case 'boolean':
      return (
        <FormControlLabel
          label={label}
          control={
            <Switch
              checked={value === true}
              onChange={(event) => onChange(event.target.checked)}
              disabled={disabled}
            />
          }
        />
      )

    case 'object': {
      if (!schema.properties || Object.keys(schema.properties).length === 0) return null
      if (depth >= MAXIMUM_DEPTH) {
        return (
          <Typography variant="caption" color="text.secondary">
            {label} cannot be edited here. Its current value is kept.
          </Typography>
        )
      }

      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2">{label}</Typography>
          <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
            <SchemaForm
              schema={schema}
              root={root}
              value={isRecord(value) ? value : {}}
              onChange={onChange}
              disabled={disabled}
              depth={depth + 1}
            />
          </Box>
        </Stack>
      )
    }

    case 'array': {
      // `isDrawable` has already established the elements are plain
      // strings; anything else fell out above. A stored value of the
      // wrong shape draws an empty list rather than throwing — the same
      // narrowing every control here does, and the same outcome: the
      // author sees nothing and nothing is claimed about what is stored
      // until they add an entry.
      const entries = Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2">{label}</Typography>
          <OptionList
            options={entries}
            // Named after the property rather than left generic, because
            // "Add an option" over a list of recipients is the sort of
            // wrong label a shared control earns if nobody passes one.
            label={`Add to ${label.toLowerCase()}`}
            disabled={disabled}
            // Emptied is `undefined` — the member goes unsent — which
            // is the same call the number control makes. It costs
            // nothing where the payload is `set`-shaped, since the fold
            // clears an absent member either way, and it is the
            // difference that matters at creation: `component.created`
            // records what the author stated, and an empty list stated
            // nothing.
            onChange={(next) => onChange(next.length === 0 ? undefined : next)}
          />
        </Stack>
      )
    }

    // A shape with no type at all. Nothing is rendered, and the
    // whole-object rule above is what keeps the stored value intact.
    default:
      return null
  }
}

/**
 * A stored value narrowed to what a date input will accept.
 *
 * A `date` field accepts a calendar day or a full timestamp — the
 * platform validates both, because a client may legitimately send either
 * spelling — so a stored timestamp has to be trimmed before the control
 * will display it. A value the input cannot render at all is passed
 * through as the empty string rather than mangled: the control shows
 * nothing, and the stored value rides through untouched, which is the
 * same rule the rest of this form follows.
 */
function dateInputValue(stored: string, format: 'date' | 'date-time'): string {
  if (format === 'date') return stored.slice(0, 10)
  return stored.length >= 16 ? stored.slice(0, 16) : stored
}

/** An empty input is an absent member, not NaN. */
function numberOrNothing(raw: string): number | undefined {
  if (raw.trim() === '') return undefined
  const parsed = Number(raw)
  return Number.isNaN(parsed) ? undefined : parsed
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * A declared default, as the text a control shows behind an empty value.
 *
 * Strings and numbers only: a boolean's default is already visible in the
 * control, and an enum's is one of its options.
 */
function placeholderFor(schema: Schema): string | undefined {
  const declared = schema.default
  if (typeof declared === 'string' && declared !== '') return declared
  if (typeof declared === 'number') return String(declared)
  return undefined
}
